import { Outlet, Link, useLocation } from 'react-router-dom';
import { lazy } from 'react';
import { 
  Users, 
  Shield, 
  Settings, 
  BarChart3,
  FileSpreadsheet,
  FolderTree,
  Zap,
  ClipboardList,
  Bot,
  ShieldCheck,
  LayoutGrid,
  Navigation,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useRoutePreloader } from '@/hooks/useRoutePreloader';

// Import functions for preloading
const adminRouteImports = {
  dashboard: () => import('@/pages/admin/AdminDashboard'),
  users: () => import('@/pages/admin/UserManagementContainer'),
  stakeholders: () => import('@/pages/admin/StakeholderManagement'),
  roles: () => import('@/pages/admin/RoleManagement'),
  permissions: () => import('@/pages/admin/PermissionMatrix'),
  'permissions-new': () => import('@/pages/admin/SimplePermissionManager'),
  projects: () => import('@/pages/admin/ProjectManagement'),
  categories: () => import('@/pages/admin/ProjectCategoriesManagement'),
  'keyplan-setup': () => import('@/pages/admin/KeyplanSetup'),
  
  'feature-flags': () => import('@/pages/FeatureFlagsPage'),
  'system-controls': () => import('@/pages/admin/SystemControls'),
  surveys: () => import('@/pages/admin/SurveyManagement'),
  'site-forms': () => import('@/pages/admin/SiteFormManagement'),
  'ai-settings': () => import('@/pages/admin/AISettings'),
  'rule-defaults': () => import('@/pages/admin/RuleDefaultsManagement'),
  'home-cards': () => import('@/pages/admin/HomeCardsManagement'),
  navigation: () => import('@/pages/admin/NavigationSettings'),
};

const navigation = [
  { name: 'Dashboard', href: '/admin', icon: BarChart3, value: 'dashboard' },
  { name: 'Users', href: '/admin/users', icon: Users, value: 'users' },
  { name: 'Stakeholders', href: '/admin/stakeholders', icon: Users, value: 'stakeholders' },
  { name: 'Roles', href: '/admin/roles', icon: Shield, value: 'roles' },
  { name: 'Permissions', href: '/admin/permissions', icon: Settings, value: 'permissions' },
  { name: 'Permissions (New)', href: '/admin/permissions-new', icon: Shield, value: 'permissions-new', badge: 'BETA' },
  { name: 'Projects', href: '/admin/projects', icon: FileSpreadsheet, value: 'projects' },
  { name: 'Categories', href: '/admin/categories', icon: FolderTree, value: 'categories' },
  { name: 'Keyplan Setup', href: '/admin/keyplan-setup', icon: Settings, value: 'keyplan-setup' },
  
  { name: 'Feature Flags', href: '/admin/feature-flags', icon: Zap, value: 'feature-flags' },
  { name: 'Surveys', href: '/admin/surveys', icon: ClipboardList, value: 'surveys' },
  { name: 'Site Forms', href: '/admin/site-forms', icon: FileSpreadsheet, value: 'site-forms' },
  { name: 'AI Settings', href: '/admin/ai-settings', icon: Bot, value: 'ai-settings' },
  { name: 'Rule Defaults', href: '/admin/rule-defaults', icon: ShieldCheck, value: 'rule-defaults' },
  { name: 'Home Cards', href: '/admin/home-cards', icon: LayoutGrid, value: 'home-cards' },
  { name: 'Navigation', href: '/admin/navigation', icon: Navigation, value: 'navigation' },
  { name: 'System', href: '/admin/system-controls', icon: Settings, value: 'system-controls' },
];

export default function AdminLayout() {
  const location = useLocation();
  const { preloadRoute, cancelPreload } = useRoutePreloader();
  
  const getCurrentTab = () => {
    if (location.pathname === '/admin') return 'dashboard';
    if (location.pathname === '/admin/users') return 'users';
    if (location.pathname === '/admin/stakeholders') return 'stakeholders';
    if (location.pathname === '/admin/roles') return 'roles';
    if (location.pathname === '/admin/permissions') return 'permissions';
    if (location.pathname === '/admin/permissions-new') return 'permissions-new';
    if (location.pathname === '/admin/projects') return 'projects';
    if (location.pathname === '/admin/categories') return 'categories';
    if (location.pathname === '/admin/keyplan-setup') return 'keyplan-setup';
    
    if (location.pathname === '/admin/feature-flags') return 'feature-flags';
    if (location.pathname === '/admin/surveys') return 'surveys';
    if (location.pathname === '/admin/site-forms') return 'site-forms';
    if (location.pathname === '/admin/ai-settings') return 'ai-settings';
    if (location.pathname === '/admin/rule-defaults') return 'rule-defaults';
    if (location.pathname === '/admin/home-cards') return 'home-cards';
    if (location.pathname === '/admin/navigation') return 'navigation';
    if (location.pathname === '/admin/system-controls') return 'system-controls';
    return 'dashboard';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation Header */}
      <div className="sticky top-0 z-50 bg-background border-b border-border">
        {/* Mobile Layout */}
        <div className="block lg:hidden">
          <div className="flex h-16 items-center justify-between px-4">
            <h1 className="text-lg font-semibold">Admin</h1>
            <Link 
              to="/" 
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <span className="hidden xs:inline">← Trang chính</span>
              <span className="xs:hidden">← Chính</span>
            </Link>
          </div>
          {/* Mobile Navigation Tabs - 3 columns */}
          <div className="px-4 pb-3">
            <Tabs value={getCurrentTab()} className="w-full">
              <TabsList className="w-full h-auto p-1 grid grid-cols-3 gap-1">
                {navigation.map((item) => (
                  <TabsTrigger 
                    key={item.value} 
                    value={item.value} 
                    asChild
                    className="flex-shrink-0 h-9"
                  >
                    <Link
                      to={item.href}
                      className="flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                      onMouseEnter={() => preloadRoute(adminRouteImports[item.value as keyof typeof adminRouteImports], item.value)}
                      onMouseLeave={() => cancelPreload(item.value)}
                      onFocus={() => preloadRoute(adminRouteImports[item.value as keyof typeof adminRouteImports], item.value, { delay: 0 })}
                    >
                      <item.icon className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                    </Link>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:block">
          <div className="flex h-16 items-center px-4 lg:px-6 max-w-[1920px] mx-auto">
            {/* Left: Admin Title */}
            <div className="flex-shrink-0 mr-3">
              <h1 className="text-base lg:text-lg font-semibold whitespace-nowrap">Admin</h1>
            </div>
            
            {/* Center: Navigation Tabs - Scrollable */}
            <div className="flex-1 min-w-0 mr-3 overflow-x-auto scrollbar-thin scrollbar-thumb-muted-foreground/30">
              <div className="relative">
                <Tabs value={getCurrentTab()} className="w-full">
                  <TabsList className="inline-flex h-10 w-auto overflow-x-auto scrollbar-thin scrollbar-thumb-muted-foreground/30 bg-muted p-1 text-muted-foreground rounded-md pe-2">
                    {navigation.map((item) => (
                      <TabsTrigger 
                        key={item.value} 
                        value={item.value} 
                        asChild
                        className="flex-shrink-0"
                      >
                        <Link
                          to={item.href}
                          className="inline-flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium whitespace-nowrap rounded-sm ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm"
                          onMouseEnter={() => preloadRoute(adminRouteImports[item.value as keyof typeof adminRouteImports], item.value)}
                          onMouseLeave={() => cancelPreload(item.value)}
                          onFocus={() => preloadRoute(adminRouteImports[item.value as keyof typeof adminRouteImports], item.value, { delay: 0 })}
                        >
                          <item.icon className="h-4 w-4 flex-shrink-0" />
                          <span className="hidden xl:inline">{item.name}</span>
                          <span className="xl:hidden">
                            {item.name === 'Keyplan Setup' ? 'Keyplan' : 
                             item.name === 'Categories' ? 'Cats' : 
                             item.name === 'Stakeholders' ? 'Stakes' :
                             item.name === 'Permissions' ? 'Perms' :
                             item.name === 'Performance' ? 'Perf' :
                             item.name}
                          </span>
                        </Link>
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
                {/* Scroll indicators - enhanced visibility */}
                <div className="absolute left-0 top-0 h-full w-8 bg-gradient-to-r from-muted via-muted/70 to-transparent pointer-events-none opacity-95" />
                <div className="absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-muted via-muted/70 to-transparent pointer-events-none opacity-95" />
              </div>
            </div>
            
            {/* Right: Back to main site - Always visible */}
            <div className="flex-shrink-0">
              <Link 
                to="/" 
                className="text-sm text-muted-foreground hover:text-foreground whitespace-nowrap flex items-center gap-1"
              >
                <span className="hidden xl:inline">← Quay về trang chính</span>
                <span className="xl:hidden">← Về</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main content - Full width */}
      <main className="p-6">
        <Outlet />
      </main>
    </div>
  );
}